/**
 * How a Question Coach mark looks (ORA-1091), set as inline styles because
 * Sass is frozen (ora-fe ADR-0001) and the marks live in DOM that React does
 * not render. A soft accent tint with an underline that sweeps in left to
 * right, distinct from the student's yellow highlight. The step being
 * narrated is stronger; earlier steps settle lighter. A stem phrase is
 * styled on its span; an answer option on its row's `.answer-choice-content`.
 */

const OPTION_CONTENT_SELECTOR = '.answer-choice-content';
/** An answer option row (`ANSWER_OPTION_ID_ATTRIBUTE` in `coachHighlights`). */
const OPTION_ROW_ATTRIBUTE = 'data-answer-option-id';
const SWEEP_MS = 400;

const accentMix = (percent: number): string =>
  `color-mix(in srgb, var(--text-accent) ${percent}%, transparent)`;

// The tint and underline colours are custom properties the gradients read,
// so the narrated step only swaps the two colours.
const TINT = '--coach-highlight-tint';
const LINE = '--coach-highlight-line';

const COLORS = {
  settled: { tint: accentMix(12), line: accentMix(45) },
  active: { tint: accentMix(26), line: 'var(--text-accent)' },
};

const BACKGROUND = `linear-gradient(var(${TINT}), var(${TINT})), linear-gradient(var(${LINE}), var(${LINE}))`;
const FULL_SIZE = '100% 100%, 100% 2px';

const STYLE_PROPERTIES = [
  TINT,
  LINE,
  'background-image',
  'background-position',
  'background-repeat',
  'background-size',
  'border-radius',
  '-webkit-box-decoration-break',
  'box-decoration-break',
];

/** The element a mark's styles go on: the stem span, or the option's text. */
const styleTarget = (mark: Element): HTMLElement | null => {
  if (mark.hasAttribute(OPTION_ROW_ATTRIBUTE)) {
    return mark.querySelector<HTMLElement>(OPTION_CONTENT_SELECTOR);
  }

  return mark instanceof HTMLElement ? mark : null;
};

const setColors = (style: CSSStyleDeclaration, isActive: boolean) => {
  const { tint, line } = isActive ? COLORS.active : COLORS.settled;
  style.setProperty(TINT, tint);
  style.setProperty(LINE, line);
};

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Styles a newly placed mark, sweeping it in unless the student asked for
 * reduced motion.
 */
export const styleCoachMark = (mark: Element, isActive: boolean): void => {
  const target = styleTarget(mark);
  if (!target) return;

  const { style } = target;
  setColors(style, isActive);
  style.setProperty('background-image', BACKGROUND);
  style.setProperty('background-position', 'left top, left bottom');
  style.setProperty('background-repeat', 'no-repeat');
  style.setProperty('background-size', FULL_SIZE);
  style.setProperty('border-radius', '2px');
  style.setProperty('-webkit-box-decoration-break', 'clone');
  style.setProperty('box-decoration-break', 'clone');

  if (prefersReducedMotion() || typeof target.animate !== 'function') return;

  target.animate(
    [{ backgroundSize: '0% 100%, 0% 2px' }, { backgroundSize: FULL_SIZE }],
    { duration: SWEEP_MS, easing: 'ease-out' }
  );
};

/** Switches a styled mark between the narrated and the settled look. */
export const setCoachMarkActiveStyle = (
  mark: Element,
  isActive: boolean
): void => {
  const target = styleTarget(mark);
  if (!target?.style.getPropertyValue(TINT)) return;

  setColors(target.style, isActive);
};

/** Takes a mark's styles off; an emptied `style` attribute goes too. */
export const unstyleCoachMark = (mark: Element): void => {
  const target = styleTarget(mark);
  if (!target) return;

  STYLE_PROPERTIES.forEach((name) => target.style.removeProperty(name));
  if (!target.getAttribute('style')?.trim()) target.removeAttribute('style');
};
